import React from "react";
import type { Map, StyleSpecification } from "@vietmap/vietmap-gl-js/dist/vietmap-gl";
import { rewriteLegacyStyleUrl } from "../libs/styles-api";
import { isAbortError, releaseStyle, removeMap } from "../libs/map-errors";

type Props = {
  /** Absolute style.json URL with apikey + consumerId already injected. */
  styleUrl: string;
  className?: string;
  /** Optional initial center as `[lng, lat]`. Defaults to centred-Vietnam. */
  center?: [number, number];
  /** Optional initial zoom. Defaults to 3.5 — wide enough to show the whole
   *  country from the northern border down to Cà Mau within a 16:9 thumb. */
  zoom?: number;
  /** Skip mounting the WebGL map until the container scrolls into view. */
  lazy?: boolean;
  /** Rewrite the loaded style before it renders (e.g. preview a palette). */
  transformStyle?: (style: StyleSpecification) => StyleSpecification;
};

type State = {
  /** Whether to actually instantiate the GL map. Toggled by IntersectionObserver. */
  shouldRender: boolean;
  /** Map failed to load — fall back to gradient/blank look. */
  error: boolean;
};

/**
 * Tiny non-interactive `vietmap-gl-js` map used as a card thumbnail. Designed
 * for a grid of many previews:
 *   - Lazy mount via IntersectionObserver (only visible cards spin up a GL ctx)
 *   - `interactive: false` → no event listeners, no input handling cost
 *   - `attributionControl: false` → thumbnails stay clean (parent card handles
 *      attribution if needed)
 *   - `transformRequest` rewrites legacy stylemap host → /styles
 *
 * When `styleUrl` changes (e.g. user picks a different consumer / apikey)
 * the map is reloaded in-place via `setStyle` to avoid recreating WebGL ctx.
 */
export default class MapPreviewThumb extends React.Component<Props, State> {
  private containerRef = React.createRef<HTMLDivElement>();
  private map: Map | null = null;
  private observer: IntersectionObserver | null = null;

  state: State = {
    shouldRender: !this.props.lazy,
    error: false,
  };

  componentDidMount() {
    if (this.props.lazy && typeof IntersectionObserver !== "undefined") {
      this.observer = new IntersectionObserver((entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            this.setState({ shouldRender: true });
            this.observer?.disconnect();
            this.observer = null;
            break;
          }
        }
      }, { rootMargin: "120px" });
      if (this.containerRef.current) {
        this.observer.observe(this.containerRef.current);
      }
    } else if (!this.props.lazy) {
      // Render immediately
      this._mountMap();
    }
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    // Just became visible → mount.
    if (!prevState.shouldRender && this.state.shouldRender) {
      this._mountMap();
    }
    // URL changed (apikey/consumer switched, etc.) → swap the style in place.
    if (prevProps.styleUrl !== this.props.styleUrl && this.map) {
      try {
        this.setState({ error: false });
        releaseStyle(this.map);
        // Keep the preview's transform (template palette / icons).
        const transform = this.props.transformStyle;
        if (transform) this.map.setStyle(this.props.styleUrl, { diff: false, transformStyle: (_prev, next) => transform(next) });
        else this.map.setStyle(this.props.styleUrl);
      } catch {
        this.setState({ error: true });
      }
    }
  }

  componentWillUnmount() {
    this.unmounted = true;
    this.observer?.disconnect();
    this.observer = null;
    if (this.map) {
      try { removeMap(this.map); } catch { /* ignore */ }
      this.map = null;
    }
  }

  private mounting = false;
  private unmounted = false;

  _mountMap = async () => {
    if (!this.containerRef.current || this.map || this.mounting || !this.props.styleUrl) return;
    this.mounting = true;
    try {
      // Loaded on first use: pages with thumbnails don't ship the map
      // library in their first bundle.
      const { default: MapLibreGl } = await import("@vietmap/vietmap-gl-js/dist/vietmap-gl");
      if (this.unmounted || !this.containerRef.current) return;
      const map = new MapLibreGl.Map({
        container: this.containerRef.current,
        // Default center is the geographic middle of Vietnam (~14.5°N, 108°E)
        // so the whole country shows on a 16:9 thumb without cutting either end.
        center: this.props.center || [108, 14.5],
        zoom: this.props.zoom ?? 3.5,
        // With a transform the real style is set below, through setStyle.
        style: this.props.transformStyle ? { version: 8, sources: {}, layers: [] } : this.props.styleUrl,
        interactive: false,
        attributionControl: false,
        // Style stored on the backend still points at the legacy /stylemap
        // host — rewrite outgoing requests transparently.
        transformRequest: (u: string) => ({ url: rewriteLegacyStyleUrl(u) }),
      });
      this.map = map;
      const transform = this.props.transformStyle;
      if (transform) map.setStyle(this.props.styleUrl, { diff: false, transformStyle: (_prev, next) => transform(next) });
      requestAnimationFrame(() => map.resize());
      map.on("load", () => map.resize());
      map.on("error", (e: any) => {
        // Unmounted (map.remove() aborts pending loads) or a cancelled request.
        if (this.map !== map || isAbortError(e)) return;
        // A tile that fails (network, disabled key) doesn't make the preview
        // unusable — only a style that can't load does.
        if (e?.tile || e?.sourceId) return;
        // eslint-disable-next-line no-console
        console.warn("[MapPreviewThumb] map error:", e?.error || e);
        this.setState({ error: true });
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("[MapPreviewThumb] mount error:", err);
      if (!this.unmounted) this.setState({ error: true });
    } finally {
      this.mounting = false;
    }
  };

  render() {
    return (
      <div
        ref={this.containerRef}
        className={this.props.className}
        aria-hidden="true"
        // When errored we expose a `data-error` attribute the parent CSS can
        // style (e.g. show a friendlier fallback gradient over the canvas).
        data-error={this.state.error || undefined}
      />
    );
  }
}
